import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import serviceApi from '../services/serviceApi';
import { PAYMENT_METHOD_LABELS } from '../config/serviceConstants';

// `defaultPaymentDate` is the server's business date for this operation (Riyadh).
// Two bugs are fixed here, and they are independent:
//  1. It used to be computed with toISOString(), which returns the UTC day — so
//     between 00:00 and 03:00 Riyadh it pre-filled YESTERDAY. On the 1st of a
//     month that records a payment into the previous month's books.
//  2. It was computed ONCE in the useState initializer with no reset when the
//     modal opened, so it also went stale on any page left open overnight —
//     wrong regardless of timezone.
const RecordPaymentModal = ({ show, onHide, subscription, defaultPaymentDate, onRecorded }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const blankForm = () => ({
    amount: '',
    method: 'bank_transfer',
    date: defaultPaymentDate || '',
    note: '',
  });

  const [form, setForm] = useState(blankForm);

  // Re-seed on every open — this is what fixes (2) above.
  useEffect(() => {
    if (show) {
      setForm(blankForm());
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, defaultPaymentDate]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleReset = () => {
    setForm(blankForm());
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);

    if (!form.amount || parseFloat(form.amount) <= 0) {
      setError('يرجى إدخال مبلغ صحيح');
      return;
    }

    setSaving(true);
    try {
      await serviceApi.updateSubscription(subscription._id, {
        payment: {
          amount: parseFloat(form.amount),
          method: form.method,
          date: form.date,
          note: form.note || undefined,
        },
      });

      handleReset();
      onRecorded();
      onHide();
    } catch (err) {
      setError(err.response?.data?.error || 'حدث خطأ — يرجى المحاولة مرة أخرى');
    } finally {
      setSaving(false);
    }
  };

  if (!subscription) return null;

  return (
    <Modal show={show} onHide={onHide} centered dir="rtl">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700, color: '#471396' }}>
          تسجيل دفعة
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: 16 }}>
          الموقع: <strong>{subscription.locationId?.name || '—'}</strong>
          {' • '}
          المستحق: <strong>{subscription.totalPrice} ر.س</strong>
        </div>

        <div className="d-flex gap-3 mb-3">
          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>المبلغ (ر.س) *</Form.Label>
            <Form.Control
              className="touch-input"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              placeholder={subscription.totalPrice?.toString() || '0'}
              dir="ltr"
            />
          </Form.Group>
          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>طريقة الدفع *</Form.Label>
            <Form.Select
              className="touch-input"
              value={form.method}
              onChange={(e) => handleChange('method', e.target.value)}
            >
              {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>تاريخ الدفع *</Form.Label>
          <Form.Control
            className="touch-input"
            type="date"
            value={form.date}
            onChange={(e) => handleChange('date', e.target.value)}
            dir="ltr"
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>ملاحظة</Form.Label>
          <Form.Control
            className="touch-input"
            as="textarea"
            rows={2}
            value={form.note}
            onChange={(e) => handleChange('note', e.target.value)}
            placeholder="اختياري..."
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>إلغاء</Button>
        <Button
          style={{ background: '#471396', borderColor: '#471396' }}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? <span className="spinner-border spinner-border-sm" /> : 'تسجيل الدفعة'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RecordPaymentModal;
